import type { AssetTemplate, AssetTemplates } from '~/modules/shopify/types';
import type { TemplateType } from '~/types/template';

import { useAssetTemplateContext, useAssetTemplatePickerContext } from '~/modules/asset-template';
import { regexTemplateIgnore, regexTemplateScheduled } from '~/constants/templateRegex';
import { PROVIDER_SOURCE_GEMPAGES, PROVIDER_SOURCE_SORT } from '~/constants/template';
import { useFilterTemplateDefault } from '~/modules/asset-template/hooks';
import { ICampaignType, IVariantType } from '~/modules/campaign/types';
import { sortByConditions, filterAssetsByType } from '~/utils';
import { findVariantByType } from '~/modules/campaign';
import { useQueryShopPlatform } from '~/modules/apps';
import { IThemePageStatus } from '~/types/themePage';
import { isThemePageDraft } from '~/utils/themePage';
import { ITemplateStatusFilter } from '../types';
import { createAssetTemplate } from '../utils';
import { useMemo } from 'react';

interface IAssetTemplateListingFiltererResult {
  templateListing: AssetTemplates | undefined;
  isAllTemplateDisabled: boolean;
  totalTemplateNotDisabled: number;
  isFetchingUnpublishedThemePages: boolean;
}

export const useAssetTemplateListingFilterer = (): IAssetTemplateListingFiltererResult => {
  const { isInstallGemPages } = useQueryShopPlatform();
  const { filterDefault } = useFilterTemplateDefault();
  const {
    assetsWithAssigned,
    themePages,
    modalAssetTemplate,
    draftAssetTemplates,
    isFetchingUnpublishedThemePages,
  } = useAssetTemplateContext();
  const { modalData, isPickVariant, isPickControl } = modalAssetTemplate;
  const {
    pageTypes,
    queryValue,
    status,
    isSelectedDisplayAll,
    isSelectedOnlyTabGP,
    isSelectedOnlyTabShopify,
    filterTemplateUnavailable,
  } = useAssetTemplatePickerContext();

  const assetsWithDraft = useMemo(() => {
    if (!assetsWithAssigned || isPickControl) return assetsWithAssigned;

    return [...assetsWithAssigned, ...draftAssetTemplates];
  }, [assetsWithAssigned, draftAssetTemplates, isPickControl]);

  const templatesFilterAssetBackup = useMemo<AssetTemplates | undefined>(() => {
    if (!assetsWithDraft) return;

    return assetsWithDraft.filter((item) => {
      if (!item.key) return false;
      if (regexTemplateIgnore.exec(item.key)) return false;
      if (regexTemplateScheduled.exec(item.key)) return false;

      return true;
    });
  }, [assetsWithDraft]);

  const templatesByProviderSource = useMemo<AssetTemplates | undefined>(() => {
    if (!templatesFilterAssetBackup) return;

    return templatesFilterAssetBackup.filter((item) => {
      if (!item.providerSource) return false;

      if (isSelectedOnlyTabShopify) {
        return !PROVIDER_SOURCE_GEMPAGES.includes(item.providerSource);
      }

      const isTemplateGemPages = PROVIDER_SOURCE_GEMPAGES.includes(item.providerSource);
      if (isSelectedOnlyTabGP) {
        if (!isInstallGemPages) return false;

        return isTemplateGemPages;
      }

      if (!isInstallGemPages && isTemplateGemPages) return false;

      return true;
    });
  }, [
    isInstallGemPages,
    isSelectedOnlyTabGP,
    isSelectedOnlyTabShopify,
    templatesFilterAssetBackup,
  ]);

  const templatesByPageMapThemePages = useMemo<AssetTemplates | undefined>(() => {
    if (!templatesByProviderSource) return;

    const templatesWithThemePageInfo = templatesByProviderSource.map((asset) => {
      if (asset.themePageInfo) return asset;
      return createAssetTemplate({ asset, themePages });
    });

    return templatesWithThemePageInfo.filter((asset) => !asset.themePageInfo?.isHide);
  }, [templatesByProviderSource, themePages]);

  const templatesByPageType = useMemo<AssetTemplates | undefined>(() => {
    if (!templatesByPageMapThemePages) return;

    const campaign = modalData?.campaign;
    const isCGTemplate = campaign?.type === ICampaignType.TEMPLATE_BUNDLE;
    if (isCGTemplate && isPickVariant) {
      const variantA = findVariantByType(campaign, IVariantType.VARIANT_A);
      const pageType = variantA?.templateType as TemplateType;
      return templatesByPageMapThemePages.filter((asset) =>
        filterAssetsByType<TemplateType>(asset, pageType),
      );
    }

    if (!pageTypes?.length) return templatesByPageMapThemePages;

    return templatesByPageMapThemePages.filter((asset) =>
      pageTypes?.some((item) => filterAssetsByType<TemplateType>(asset, item)),
    );
  }, [templatesByPageMapThemePages, modalData?.campaign, isPickVariant, pageTypes]);

  const templatesByStatus = useMemo<AssetTemplates | undefined>(() => {
    if (!templatesByPageType) return;
    if (!status?.length) return templatesByPageType;

    return templatesByPageType.filter((asset) => {
      const pageStatus = asset.themePageInfo?.status;
      return status.some((s) => {
        if (s === ITemplateStatusFilter.ACTIVE) {
          const isActiveFromShopify = !pageStatus;
          const isActiveFromGP = pageStatus === IThemePageStatus.PUBLISHED;

          return isActiveFromShopify || isActiveFromGP;
        }
        if (s === ITemplateStatusFilter.DRAFT)
          return isThemePageDraft(pageStatus, asset.themePageInfo?.publishedAt) && !isPickControl;
        if (s === ITemplateStatusFilter.NEED_REPUBLISHING)
          return pageStatus === IThemePageStatus.NEED_PUBLISHING;
        return false;
      });
    });
  }, [templatesByPageType, status, isPickControl]);

  const templateAvailable = useMemo<AssetTemplates | undefined>(() => {
    if (!templatesByStatus) return;

    return filterTemplateUnavailable({ templates: templatesByStatus, isSelectedDisplayAll });
  }, [filterTemplateUnavailable, isSelectedDisplayAll, templatesByStatus]);

  const templateSearchedByQueryValue = useMemo<AssetTemplates | undefined>(() => {
    if (!templateAvailable) return;

    const lowercasedQuery = (queryValue || '').toLowerCase();
    return templateAvailable.filter((asset) => {
      const lowercasedAssetKey = asset.key?.toLowerCase();
      const lowercasedLabel = asset.themePageInfo?.label?.toLowerCase();

      const isMatchedAssetKey = lowercasedAssetKey?.includes(lowercasedQuery);
      const isMatchedLabel = lowercasedLabel?.includes(lowercasedQuery);
      const isMatchedAssignedData = asset.assigned?.data?.some(
        ({ title, url }) =>
          title?.toLowerCase().includes(lowercasedQuery) ||
          url?.toLowerCase().includes(lowercasedQuery),
      );

      return isMatchedAssetKey || isMatchedLabel || isMatchedAssignedData;
    });
  }, [queryValue, templateAvailable]);

  const templatesByDefault = useMemo<AssetTemplates | undefined>(() => {
    if (!templateSearchedByQueryValue) return;
    if (!isPickVariant) return templateSearchedByQueryValue;

    const templateDefaults =
      templatesByPageType?.filter((item) => item.themePageInfo?.default) || [];
    return filterDefault({ templates: templateSearchedByQueryValue, templateDefaults });
  }, [templateSearchedByQueryValue, filterDefault, isPickVariant, templatesByPageType]);

  const templateListingFinally = useMemo<AssetTemplates | undefined>(() => {
    if (!templatesByDefault) return;

    return sortByConditions<AssetTemplate>(templatesByDefault, [
      { attr: 'updated_at', order: 'asc' },
      {
        attr: 'providerSource',
        order: 'desc',
        orderArrayAttr: 'providerSource',
        orderArray: {
          desc: PROVIDER_SOURCE_SORT,
          asc: PROVIDER_SOURCE_SORT,
        },
      },
    ]);
  }, [templatesByDefault]);

  const isAllTemplateDisabled = useMemo(() => {
    if (!templateListingFinally) return false;

    return templateListingFinally.every((item) => item.disabled);
  }, [templateListingFinally]);

  const totalTemplateNotDisabled = useMemo(() => {
    return templateListingFinally?.filter((item) => !item.disabled).length || 0;
  }, [templateListingFinally]);

  return useMemo(() => {
    return {
      templateListing: templateListingFinally,
      isAllTemplateDisabled,
      totalTemplateNotDisabled,
      isFetchingUnpublishedThemePages,
    };
  }, [
    templateListingFinally,
    isAllTemplateDisabled,
    totalTemplateNotDisabled,
    isFetchingUnpublishedThemePages,
  ]);
};
